import React from 'react';
import { CardMedia, Typography, Card, CardContent, styled } from '@mui/material';
import { HighlightCardProps, TopRanking } from './HighlightCard.props';
import { images } from '../../constants/images';
import { formatNumber } from '../../utils/util';

const StyledCard = styled(Card)(() => ({
  display: 'grid',
  gridTemplateColumns: '1fr 110px',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#193051',
  border: '1px solid #CCCFCF',
  borderRadius: '8px',
  padding: '24px',
  boxShadow: '0px 0px 4px 0px #00000040',
  width: '285.5px',
  height: '108px',
  '&:nth-of-type(1)': {
    padding: '40px',
    order: 2,
    img: {
      width: '108px',
      height: '108px'
    }
  },
  '&:nth-of-type(2)': {
    padding: '28px',
    order: 1,
    img: {
      width: '88px',
      height: '88px'
    }
  },
  '&:nth-of-type(3)': {
    padding: '24px',
    order: 3,
    img: {
      width: '72px',
      height: '72px'
    }
  }
}));

const StyledCardContent = styled(CardContent)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  flex: 1,
  padding: 0
}));

const StyledNameTypography = styled(Typography)(() => ({
  fontFamily: 'Righteous',
  fontSize: '16px',
  fontStyle: 'normal',
  color: '#5EBEC2',
  fontWeight: 400,
  lineHeight: '20px',
  marginBottom: '7px',
  wordBreak: 'break-all',
  display: '-webkit-box',
  WebkitLineClamp: 1,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
}));

const StyledPointsTypography = styled(Typography)(() => ({
  color: '#FFFFFF',
  opacity: 0.9,
  fontWeight: 500,
  fontSize: '36px',
  lineHeight: '49px',
  marginBottom: '7px'
}));

const StyledFieldTypography = styled(Typography)(() => ({
  color: '#FFFFFF',
  opacity: 0.9,
  fontWeight: 700,
  fontSize: '10px',
  lineHeight: '12px',
  marginBottom: '2.5px'
}));

const StyledHighlightTypeTypography = styled(Typography)(() => ({
  color: '#FFFFFF',
  opacity: 0.9,
  fontWeight: 600,
  fontSize: '10px',
  lineHeight: '12px'
}));

const StyledCardMedia = styled(CardMedia)({
  display: 'flex',
  width: 'auto',
  margin: 'auto'
});

const badgeIconMapping: Record<TopRanking, string> = {
  [TopRanking.FIRST]: images.BadgeGoldIcon,
  [TopRanking.SECOND]: images.BadgeSilverIcon,
  [TopRanking.THIRD]: images.BadgeBronzeIcon
};

export const HighlightCard: React.FC<HighlightCardProps> = ({
  name,
  rank,
  points,
  point_type,
  metric_type,
  sx
}) => {
  const getFontSizeByLength = (length: number) => {
    const BASE_LENGTH = 7;
    const MAX_FONT_SIZE = 36;
    const MIN_FONT_SIZE = 20;
    const SHRINK_RATE = 2;

    if (length <= BASE_LENGTH) return `${MAX_FONT_SIZE}px`;
    const scaledSize = MAX_FONT_SIZE - (length - BASE_LENGTH) * SHRINK_RATE;
    return `${Math.max(scaledSize, MIN_FONT_SIZE)}px`;
  };

  return (
    <StyledCard sx={sx}>
      <StyledCardContent>
        <StyledNameTypography>{name}</StyledNameTypography>
        <StyledPointsTypography sx={{ fontSize: getFontSizeByLength(String(points).length) }}>
          {typeof points === 'number' ? formatNumber(points) : points}
        </StyledPointsTypography>
        <StyledFieldTypography>{point_type}</StyledFieldTypography>
        <StyledHighlightTypeTypography>{metric_type}</StyledHighlightTypeTypography>
      </StyledCardContent>
      <StyledCardMedia as="img" src={badgeIconMapping[rank as TopRanking]} />
    </StyledCard>
  );
};
